// SendItFolder.jsx
import React from 'react';
import html2pdf from 'html2pdf.js';
import { Document, Packer, Paragraph, TextRun } from 'docx';
import * as XLSX from 'xlsx';

export const sendItFolderMessage = async () => {
    const previewElements = document.querySelectorAll('.print-preview-container .print-preview');

    if (previewElements.length === 0) {
        alert('No content found to save.');
        return;
    }

    // Get filename
    const fileNameInput = document.getElementById('fileNameInput');
    let fileName = 'OneWriter-Export';
    if (fileNameInput && fileNameInput.value.trim()) {
        fileName = fileNameInput.value.trim();
    }

    let combinedHTML = '';
    let combinedText = '';

    previewElements.forEach(preview => {
        combinedHTML += preview.innerHTML + '<br><br>';
        combinedText += (preview.innerText || preview.textContent || '') + '\n\n';
    });

    try {
        // Create HTML file and download it
        const htmlBlob = new Blob([`
<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <title>${fileName}</title>
    <style>
        body { font-family: Arial, sans-serif; margin: 20px; line-height: 1.6; }
    </style>
</head>
<body>
    <h1>${fileName}</h1>
    ${combinedHTML}
</body>
</html>`], { type: 'text/html' });

        const url = URL.createObjectURL(htmlBlob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${fileName}.html`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        console.log(`Downloaded: ${fileName}.html`);
    } catch (err) {
        console.error('HTML file creation failed:', err);
        alert(`Failed to create HTML file: ${err.message}`);
    }
};

export const sendItFolderPDF = (orientationMode = 'portrait') => {
    const previewElements = document.querySelectorAll('.print-preview-container .print-preview');

    if (previewElements.length === 0) {
        alert('No content found to generate PDF.');
        return;
    }

    // Get filename
    const fileNameInput = document.getElementById('fileNameInput');
    let fileName = 'OneWriter-Export';
    if (fileNameInput && fileNameInput.value.trim()) {
        fileName = fileNameInput.value.trim();
    }

    console.log(`PDF filename will be: ${fileName}.pdf`);

    const container = document.createElement('div');
    container.style.background = 'white';
    container.style.width = orientationMode === 'landscape' ? '11in' : '8.5in';

    previewElements.forEach((preview, index) => {
        const clone = preview.cloneNode(true);

        // Remove the document frame
        clone.style.border = 'none';

        const contentElements = clone.querySelectorAll('.print-preview-content');
        contentElements.forEach(contentEl => {
            contentEl.style.maxWidth = 'none';
            contentEl.style.width = 'auto';
        });

        clone.style.width = '100%';
        clone.style.minHeight = orientationMode === 'landscape' ? '7.5in' : '10in';
        clone.style.margin = '0';
        clone.style.padding = '0.5in';
        clone.style.boxSizing = 'border-box';
        clone.style.display = 'block';
        clone.style.fontSize = '16px';
        clone.style.lineHeight = '1.5';

        if (index < previewElements.length - 1) {
            clone.style.pageBreakAfter = 'always';
        }

        container.appendChild(clone);
    });

    document.body.appendChild(container);

    const opt = {
        margin: 0,
        filename: `${fileName}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: {
            scale: 2,
            useCORS: true,
            allowTaint: true,
            letterRendering: true
        },
        jsPDF: {
            unit: 'in',
            format: 'letter',
            orientation: orientationMode
        }
    };

    html2pdf()
        .set(opt)
        .from(container)
        .outputPdf('blob')
        .then((pdfBlob) => {
            // Just download the PDF, no email
            const url = URL.createObjectURL(pdfBlob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${fileName}.pdf`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            container.remove();

            console.log(`Downloaded: ${fileName}.pdf`);
        })
        .catch((err) => {
            console.error('PDF generation failed:', err);
            container.remove();
        });
};

export const sendItFolderDocx = async () => {
    const previewElements = document.querySelectorAll('.print-preview-container .print-preview');

    if (previewElements.length === 0) {
        alert('No content found to generate DOCX.');
        return;
    }

    // Get filename
    const fileNameInput = document.getElementById('fileNameInput');
    let fileName = 'OneWriter-Export';
    if (fileNameInput && fileNameInput.value.trim()) {
        fileName = fileNameInput.value.trim();
    }

    // Extract text content
    let combinedText = '';
    previewElements.forEach((preview, index) => {
        const textContent = preview.innerText || preview.textContent || '';
        combinedText += textContent;
        if (index < previewElements.length - 1) {
            combinedText += '\n\n--- Page Break ---\n\n';
        }
    });

    console.log('Combined text for DOCX:', combinedText.substring(0, 200));

    if (!combinedText.trim()) {
        alert('No text content found in preview elements.');
        return;
    }

    try {
        // Create DOCX document
        const paragraphs = combinedText.split('\n').map(line => {
            return new Paragraph({
                children: [
                    new TextRun({
                        text: line || ' ',
                        font: 'Arial',
                        size: 24,
                    }),
                ],
            });
        });

        const doc = new Document({
            sections: [{
                properties: {},
                children: [
                    new Paragraph({
                        children: [
                            new TextRun({
                                text: fileName,
                                font: 'Arial',
                                size: 32,
                                bold: true,
                            }),
                        ],
                    }),
                    new Paragraph({ children: [new TextRun(' ')] }),
                    ...paragraphs,
                ],
            }],
        });

        // Generate and download DOCX
        const docxBlob = await Packer.toBlob(doc);
        const url = URL.createObjectURL(docxBlob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${fileName}.docx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        console.log(`Downloaded: ${fileName}.docx`);

    } catch (err) {
        console.error('DOCX generation failed:', err);
        alert(`Failed to generate DOCX file: ${err.message}`);
    }
};

export const sendItFolderXlsx = async () => {
    const previewElements = document.querySelectorAll('.print-preview-container .print-preview');

    if (previewElements.length === 0) {
        alert('No content found to generate XLSX.');
        return;
    }

    // Get filename
    const fileNameInput = document.getElementById('fileNameInput');
    let fileName = 'OneWriter-Export';
    if (fileNameInput && fileNameInput.value.trim()) {
        fileName = fileNameInput.value.trim();
    }

    console.log(`XLSX filename will be: ${fileName}.xlsx`);

    try {
        // Create a new workbook
        const wb = XLSX.utils.book_new();

        // Collect all table data with their card titles (same logic as email version)
        const tableDataByTitle = new Map();
        let globalHeaders = null;

        previewElements.forEach((preview, pageIndex) => {
            const cardTitles = preview.querySelectorAll('.card-title, .title, h1, h2, h3, h4, h5, h6');
            const tables = preview.querySelectorAll('table');

            if (tables.length > 0) {
                tables.forEach((table, tableIndex) => {
                    let cardTitle = 'General';

                    if (cardTitles.length > 0) {
                        for (let i = cardTitles.length - 1; i >= 0; i--) {
                            const titleElement = cardTitles[i];
                            const titleText = (titleElement.innerText || titleElement.textContent || '').trim();

                            const titlePosition = titleElement.compareDocumentPosition(table);
                            if (titlePosition & Node.DOCUMENT_POSITION_FOLLOWING) {
                                cardTitle = titleText;
                                break;
                            }
                        }
                    }

                    console.log(`Page ${pageIndex + 1}, Table ${tableIndex + 1}: Card title = "${cardTitle}"`);

                    if (!globalHeaders) {
                        const headerRows = table.querySelectorAll('thead tr, tr:first-child');
                        if (headerRows.length > 0) {
                            const headerRow = headerRows[0];
                            const headerCells = headerRow.querySelectorAll('th, td');
                            globalHeaders = Array.from(headerCells).map(cell =>
                                (cell.innerText || cell.textContent || '').trim()
                            );
                        }
                    }

                    const bodyRows = table.querySelectorAll('tbody tr, tr:not(:first-child)');
                    const tableRowData = [];

                    bodyRows.forEach(row => {
                        const cells = row.querySelectorAll('td, th');
                        const rowData = Array.from(cells).map(cell => {
                            return (cell.innerText || cell.textContent || '').trim();
                        });

                        if (rowData.some(cell => cell)) {
                            tableRowData.push(rowData);
                        }
                    });

                    if (!globalHeaders && tableRowData.length === 0) {
                        const allRows = table.querySelectorAll('tr');
                        allRows.forEach((row, rowIndex) => {
                            const cells = row.querySelectorAll('td, th');
                            const rowData = Array.from(cells).map(cell =>
                                (cell.innerText || cell.textContent || '').trim()
                            );
                            if (rowData.some(cell => cell)) {
                                if (rowIndex === 0 && !globalHeaders) {
                                    globalHeaders = rowData;
                                } else {
                                    tableRowData.push(rowData);
                                }
                            }
                        });
                    }

                    if (tableDataByTitle.has(cardTitle)) {
                        const existingData = tableDataByTitle.get(cardTitle);
                        existingData.push(...tableRowData);
                    } else {
                        tableDataByTitle.set(cardTitle, [...tableRowData]);
                    }
                });
            } else {
                const textContent = preview.innerText || preview.textContent || '';
                const lines = textContent.split('\n').filter(line => line.trim());

                if (!globalHeaders) {
                    globalHeaders = ['Content'];
                }

                const textRows = lines.map(line => [line.trim()]).filter(row => row[0]);
                const cardTitle = 'Text Content';

                if (tableDataByTitle.has(cardTitle)) {
                    const existingData = tableDataByTitle.get(cardTitle);
                    existingData.push(...textRows);
                } else {
                    tableDataByTitle.set(cardTitle, textRows);
                }
            }
        });

        // Combine data into worksheet
        const worksheetData = [];

        if (globalHeaders && globalHeaders.length > 0) {
            worksheetData.push(globalHeaders);
        }

        for (const [cardTitle, rowData] of tableDataByTitle) {
            worksheetData.push(...rowData);
        }

        if (worksheetData.length === 0) {
            worksheetData.push(['No data found']);
        }

        const ws = XLSX.utils.aoa_to_sheet(worksheetData);

        const colWidths = [];
        worksheetData.forEach(row => {
            row.forEach((cell, colIndex) => {
                const cellLength = String(cell).length;
                colWidths[colIndex] = Math.max(colWidths[colIndex] || 10, Math.min(cellLength + 2, 50));
            });
        });

        ws['!cols'] = colWidths.map(width => ({ width }));
        XLSX.utils.book_append_sheet(wb, ws, 'OneWriter Export');

        const xlsxArrayBuffer = XLSX.write(wb, {
            bookType: 'xlsx',
            type: 'array'
        });

        const xlsxBlob = new Blob([xlsxArrayBuffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });

        // Just download the XLSX, no email
        const url = URL.createObjectURL(xlsxBlob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${fileName}.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        console.log(`Downloaded: ${fileName}.xlsx`);

    } catch (err) {
        console.error('XLSX generation failed:', err);
        alert(`Failed to generate XLSX file: ${err.message}`);
    }
};